import {
  IDEAL_PROCEDURE_FAMILIES,
  type IdealItemDto,
  type IdealProcedureFamily,
  type IdealRhythm,
  type StepGuideDto,
  type WizardStepKey,
} from '@bk/contracts';
import { ownerError } from '@bk/contracts';
import { Checks } from './Duty';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';
import { Button, ErrorText } from '../../../ui';
import { ActivityLoader } from '../Onboarding';
import { StepIntro } from './Illustration';
import { useTalk } from './Talk';
import { AddedItems } from './AddedItems';
import { BrainNotes } from './StepReview';
import { ItemBase } from './Labels';
import { LibrarySources } from './LibrarySources';

const RHYTHM: Record<IdealRhythm, string> = {
  giorno: 'Ogni giorno',
  settimana: 'Ogni settimana',
  mese: 'Ogni mese',
  trimestre: 'Ogni trimestre',
  semestre: 'Ogni sei mesi',
  anno: 'Ogni anno',
  evento: 'Quando serve',
  continuo: 'Sempre attiva',
};

/** I tre criteri del primo piano (ADR 0054): li sceglie il motore, il codice li disegna. */
const FAMILY: Record<
  IdealProcedureFamily,
  {
    titolo: string;
    testo: string;
    ink: string;
    line: string;
    wash: string;
    ring: string;
    icon: ReactNode;
  }
> = {
  uso: {
    titolo: 'Le più usate',
    testo: 'Girano più spesso e tengono in piedi il lavoro di ogni giorno.',
    ink: 'text-accent',
    line: 'border-accent',
    wash: 'bg-accent/[0.07]',
    ring: 'hover:ring-accent',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M20 12a8 8 0 1 1-2.34-5.66" />
        <path d="M20 4v5h-5" />
      </svg>
    ),
  },
  critica: {
    titolo: 'Le più critiche',
    testo: 'Se saltano l’azienda si ferma, perde clienti o paga.',
    ink: 'text-orange-700',
    line: 'border-orange-700',
    wash: 'bg-orange-700/[0.07]',
    ring: 'hover:ring-orange-700',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      >
        <path d="M12 3 2.5 20h19L12 3Z" />
        <path d="M12 10v4M12 17.2v.1" strokeLinecap="round" />
      </svg>
    ),
  },
  valore: {
    titolo: 'Il valore aggiunto',
    testo: 'Le fanno le aziende migliori del settore e rendono più competitivi.',
    ink: 'text-teal-700',
    line: 'border-teal-700',
    wash: 'bg-teal-700/[0.07]',
    ring: 'hover:ring-teal-700',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 17 10 11l4 4 6-7" />
        <path d="M15 8h5v5" />
      </svg>
    ),
  },
};
const DOT: Record<IdealProcedureFamily, string> = {
  uso: 'bg-accent',
  critica: 'bg-orange-700',
  valore: 'bg-teal-700',
};

/** Un ambito della struttura portante: l'impresa, la sede in sé, oppure un'attività economica in una sede. */
interface Scope {
  key: string;
  /** Il nome per esteso: la sede e l'attività, o il luogo della sede. */
  titolo: string;
  /** Dove sta (ADR 0054): «impresa» o la sede; i filtri si raggruppano così. */
  luogo: string;
  /** Il nome dentro il suo luogo, per i filtri della seconda riga. */
  breve: string;
  voci: IdealItemDto[];
}
interface Item {
  id: string;
  v: IdealItemDto;
  scope: Scope;
}

/** Il nome dei passi, per dire dove rivedere ciò che non torna (ADR 0055). */
const cap = (s: string | null) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

/**
 * Passo «Procedure» (ADR 0038, 0054): il cervello progetta tutte le procedure dell'azienda ideale dai dati dei passi
 * precedenti e mette in primo piano quelle che contano di più (le più usate, le più critiche, quelle di valore aggiunto),
 * con il perché; le altre si vedono con «Vedi tutte». Ogni procedura si apre a lato sullo standard: come si fa, con quali
 * strumenti, chi ne risponde, cosa lascia, perché c'è. Niente persone e niente da togliere; nessun avviso quando il
 * progetto si aggiorna in background.
 */
export function ProceduresStep({
  next,
  onNext,
  finish,
}: {
  next: { titolo: string } | null;
  onNext: () => void;
  /** Ultimo passo (ADR 0040): finisce la configurazione guidata e apre «La tua azienda». */
  finish: (() => void) | null;
}) {
  const { t, tenant, isManager } = useTenant();
  const qc = useQueryClient();
  const guide = useTenantQuery<StepGuideDto>(['guide', 'procedure'], '/onboarding/guide/procedure');
  const refresh = useMutation({
    mutationFn: () =>
      api<StepGuideDto>(t('/onboarding/guide/procedure/refresh'), { method: 'POST' }),
    onSuccess: (g) => qc.setQueryData(['tenant', tenant.id, 'guide', 'procedure'], g),
  });
  const d = guide.data?.ideale ?? null;
  // Le risposte del titolare alle domande del cervello (ADR 0055) vanno nella chat del passo: il cervello le legge e
  // aggiorna il progetto dal precedente, in background, senza avvisi.
  const scopes: Scope[] = [
    ...(d?.impresa?.voci.length
      ? [
          {
            key: 'impresa',
            titolo: 'Tutta l’impresa',
            luogo: 'impresa',
            breve: 'Tutta l’impresa',
            voci: d.impresa.voci,
          },
        ]
      : []),
    ...(d?.sedi ?? []).flatMap((s) => [
      ...(s.sede_in_se?.voci.length
        ? [
            {
              key: `${s.sede_id}:sede`,
              titolo: `${s.sede} · il luogo`,
              luogo: s.sede_id,
              breve: 'Il luogo',
              voci: s.sede_in_se.voci,
            },
          ]
        : []),
      ...s.attivita
        .filter((a) => a.voci.length)
        .map((a) => ({
          key: `${s.sede_id}:${a.attivita}`,
          titolo: `${s.sede} · ${a.attivita}`,
          luogo: s.sede_id,
          breve: a.attivita,
          voci: a.voci,
        })),
    ]),
  ];
  const items: Item[] = scopes.flatMap((s) =>
    s.voci.map((v, i) => ({ id: `${s.key}|${i}`, v, scope: s })),
  );
  const top = items.filter((x) => x.v.famiglia);
  const law = items.filter((x) => x.v.obbligatorio).length;
  const activities = new Set(d?.sedi.flatMap((s) => s.attivita.map((a) => a.attivita)) ?? []).size;
  // Le pagine che il cervello ha consultato con la ricerca sul web (ADR 0054), una volta ciascuna.
  const sources = [
    ...new Set([...(d?.impresa?.fonti ?? []), ...(d?.sedi ?? []).flatMap((s) => s.fonti ?? [])]),
  ];
  const notes = [
    ...new Set([
      ...(d?.impresa?.note ?? []),
      ...(d?.sedi ?? []).flatMap((s) => [
        ...(s.sede_in_se?.note ?? []),
        ...s.attivita.flatMap((a) => a.note),
      ]),
    ]),
  ];
  const updating =
    refresh.isPending ||
    !!d?.impresa?.generating ||
    !!d?.sedi.some((s) => s.generating) ||
    !!d?.primo_piano?.generating;
  const error =
    d?.impresa?.error ?? d?.sedi.find((s) => s.error)?.error ?? d?.primo_piano?.error ?? null;

  const [all, setAll] = useState(false);
  const [placeKey, setPlaceKey] = useState<string | null>(null);
  const [scopeKey, setScopeKey] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const talk = useTalk();
  const opened = items.find((x) => x.id === openId) ?? null;

  // Senza primo piano (progetto di prima o scelta non ancora arrivata) l'elenco completo è aperto.
  const showAll = all || !top.length;
  // I filtri per luogo (ADR 0054): prima l'impresa e le sedi; dentro una sede il suo luogo e le sue attività.
  const places = [
    ...new Map(
      scopes.map((sc) => [
        sc.luogo,
        {
          key: sc.luogo,
          titolo:
            sc.luogo === 'impresa'
              ? 'Tutta l’impresa'
              : (d?.sedi.find((x) => x.sede_id === sc.luogo)?.sede ?? sc.luogo),
          n: scopes.filter((x) => x.luogo === sc.luogo).reduce((t, x) => t + x.voci.length, 0),
        },
      ]),
    ).values(),
  ];
  const inPlace = scopes.filter((sc) => sc.luogo === placeKey);
  const scope = scopes.find((sc) => sc.key === scopeKey && sc.luogo === placeKey) ?? null;
  const byGroup = (rows: Item[]) =>
    [...new Set(rows.map((x) => cap(x.v.gruppo) ?? 'Altre'))].map((g) => ({
      titolo: g,
      rows: rows.filter((x) => (cap(x.v.gruppo) ?? 'Altre') === g),
    }));
  const blocks = scope
    ? byGroup(items.filter((x) => x.scope === scope))
    : placeKey === 'impresa'
      ? byGroup(items.filter((x) => x.scope.luogo === 'impresa'))
      : placeKey
        ? inPlace.map((sc) => ({ titolo: sc.breve, rows: items.filter((x) => x.scope === sc) }))
        : places.map((p) => ({
            titolo: p.titolo,
            rows: items.filter((x) => x.scope.luogo === p.key),
          }));

  return (
    <div className="space-y-8 pb-8 pt-2">
      <StepIntro step="procedure" title="Le procedure che fanno girare la tua azienda">
        {items.length > 0 ? (
          <p className="mt-1 max-w-2xl text-sm text-ink/60">
            Ho progettato <b className="font-semibold text-ink">{items.length} procedure</b>{' '}
            incrociando sedi{activities > 0 && `, ${activities} attività`}, impianti e programmi
            {law > 0 && (
              <>
                : <b className="font-semibold text-ink">{law}</b> le impone una norma
              </>
            )}
            .{' '}
            {top.length > 0 && (
              <>
                Qui vedi per prime le <b className="font-semibold text-ink">{top.length}</b> che
                contano di più per un’azienda come la tua, con il motivo e gli strumenti con cui si
                fanno.
              </>
            )}
          </p>
        ) : (
          <p className="mt-1 text-sm text-ink/60">
            Progetto le procedure incrociando ciò che so di impresa, sedi, attività, impianti,
            strumenti e ruoli.
          </p>
        )}
      </StepIntro>

      <BrainNotes step="procedure" d={d} what="le procedure" />

      {!updating && error && (
        <p className="text-sm text-red-700">
          Il progetto delle procedure non è riuscito: {ownerError(error)}{' '}
          {isManager && (
            <button className="text-accent hover:underline" onClick={() => refresh.mutate()}>
              Riprova
            </button>
          )}
        </p>
      )}
      {/* Senza procedure la pagina dice cosa succede: il cervello sta progettando, oppure il progetto non è partito. */}
      {!items.length && updating && (
        <ActivityLoader activity={null} fallback="Progetto le procedure della tua azienda…" />
      )}
      {!items.length && !updating && !error && guide.data && (
        <p className="text-sm text-ink/60">
          Le procedure non sono ancora state progettate.{' '}
          {isManager && (
            <button className="text-accent hover:underline" onClick={() => refresh.mutate()}>
              Progettale ora
            </button>
          )}
        </p>
      )}
      {(refresh.error || guide.error) && (
        <ErrorText>{errorMessage(refresh.error ?? guide.error)}</ErrorText>
      )}

      {top.length > 0 && (
        <section aria-labelledby="primo-piano">
          <div className="mb-3.5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h3 id="primo-piano" className="text-base font-semibold">
              In primo piano
            </h3>
            <span className="text-xs text-ink/45">Tocca una procedura per vedere come si fa</span>
          </div>
          <div className="grid items-start gap-4 md:grid-cols-3">
            {IDEAL_PROCEDURE_FAMILIES.map((k) => {
              const f = FAMILY[k];
              const xs = top.filter((x) => x.v.famiglia === k);
              return (
                <div key={k} className="grid gap-2.5">
                  <div
                    className={`grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2.5 border-b-2 px-0.5 pb-1.5 ${f.line}`}
                  >
                    <span className={`row-span-2 h-5 w-5 ${f.ink}`} aria-hidden="true">
                      {f.icon}
                    </span>
                    <strong className="text-sm">{f.titolo}</strong>
                    <span className={`row-span-2 text-xl font-bold tabular-nums ${f.ink}`}>
                      {xs.length}
                    </span>
                    <small className="text-xs leading-snug text-ink/55">{f.testo}</small>
                  </div>
                  {xs.length ? (
                    xs.map((x) => (
                      <button
                        key={x.id}
                        onClick={() => setOpenId(x.id)}
                        className={`grid gap-1.5 rounded-2xl bg-white px-4 py-3.5 text-left shadow-sm ring-1 ring-black/5 transition hover:-translate-y-px ${f.ring}`}
                      >
                        <span className="font-semibold leading-snug">{x.v.nome}</span>
                        {x.v.rilievo && (
                          <span className="text-[13px] leading-snug text-ink/60">
                            {x.v.rilievo}
                          </span>
                        )}
                        <span className="flex flex-wrap gap-x-2.5 gap-y-0.5 text-xs text-ink/45">
                          <span>{x.scope.titolo}</span>
                          {x.v.ritmo && <span>{RHYTHM[x.v.ritmo]}</span>}
                          {!!x.v.strumenti?.length && (
                            <span className={`font-medium ${f.ink}`}>
                              {x.v.strumenti.length === 1
                                ? '1 strumento'
                                : `${x.v.strumenti.length} strumenti`}
                            </span>
                          )}
                        </span>
                      </button>
                    ))
                  ) : (
                    <p className="px-0.5 py-2 text-[13px] text-ink/45">Nessuna per ora.</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {items.length > 0 && (
        <section className="grid gap-3.5">
          {top.length > 0 && (
            <button
              aria-expanded={all}
              onClick={() => setAll(!all)}
              className="inline-flex items-center gap-2.5 justify-self-start rounded-xl bg-white px-4 py-2.5 text-sm font-medium ring-1 ring-black/10 hover:ring-accent"
            >
              <span className={`text-ink/40 transition-transform ${all ? 'rotate-90' : ''}`}>
                ›
              </span>
              {all ? 'Nascondi l’elenco completo' : `Vedi tutte le ${items.length} procedure`}
            </button>
          )}
          {showAll && (
            <>
              <div className="grid gap-2">
                <div className="flex flex-wrap gap-1.5" role="group" aria-label="Dove si svolge">
                  {[{ key: null, titolo: 'Tutte', n: items.length }, ...places].map((p) => (
                    <button
                      key={p.key ?? 'tutte'}
                      aria-pressed={placeKey === p.key}
                      onClick={() => {
                        setPlaceKey(p.key);
                        setScopeKey(null);
                      }}
                      title={p.titolo}
                      className={`max-w-[22rem] truncate rounded-full px-3 py-1.5 text-[13px] ${placeKey === p.key ? 'bg-ink text-paper' : 'bg-white text-ink/65 ring-1 ring-black/10'}`}
                    >
                      {p.titolo}
                      <span className="ml-1 tabular-nums opacity-60">{p.n}</span>
                    </button>
                  ))}
                </div>
                {placeKey && placeKey !== 'impresa' && inPlace.length > 1 && (
                  <div
                    className="flex flex-wrap gap-1.5 border-l-2 border-black/10 pl-3"
                    role="group"
                    aria-label="Nella sede"
                  >
                    {[
                      {
                        key: null,
                        breve: 'Tutto',
                        titolo: 'Tutto',
                        n: inPlace.reduce((t, x) => t + x.voci.length, 0),
                      },
                      ...inPlace.map((sc) => ({
                        key: sc.key,
                        breve: sc.breve,
                        titolo: sc.titolo,
                        n: sc.voci.length,
                      })),
                    ].map((sc) => (
                      <button
                        key={sc.key ?? 'tutto'}
                        aria-pressed={scopeKey === sc.key}
                        onClick={() => setScopeKey(sc.key)}
                        title={sc.titolo}
                        className={`max-w-[18rem] truncate rounded-full px-2.5 py-1 text-xs ${scopeKey === sc.key ? 'bg-accent text-white' : 'bg-white text-ink/65 ring-1 ring-black/10'}`}
                      >
                        {sc.breve}
                        <span className="ml-1 tabular-nums opacity-60">{sc.n}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid items-start gap-4 md:grid-cols-2">
                {blocks.map((b) => (
                  <div key={b.titolo}>
                    <h4 className="mb-1.5 px-1 text-xs font-semibold text-ink/60">
                      {b.titolo} <span className="font-normal text-ink/40">{b.rows.length}</span>
                    </h4>
                    <div className="divide-y divide-black/5 overflow-hidden rounded-2xl bg-white ring-1 ring-black/5">
                      {b.rows.map((x) => (
                        <button
                          key={x.id}
                          onClick={() => setOpenId(x.id)}
                          className="grid w-full grid-cols-[10px_minmax(0,1fr)_auto] items-center gap-x-2.5 gap-y-0.5 px-3.5 py-2.5 text-left hover:bg-black/[0.02]"
                        >
                          <span
                            className={`h-2 w-2 rounded-full ${x.v.famiglia ? DOT[x.v.famiglia] : 'ring-1 ring-inset ring-black/15'}`}
                            title={x.v.famiglia ? FAMILY[x.v.famiglia].titolo : undefined}
                          />
                          <span className="min-w-0 text-sm font-medium">{x.v.nome}</span>
                          <span className="col-start-3 row-span-2">
                            {x.v.base || x.v.obbligatorio ? (
                              <ItemBase v={x.v} />
                            ) : (
                              x.v.ritmo && (
                                <span className="whitespace-nowrap rounded-full bg-black/[0.04] px-2 py-0.5 text-[11.5px] text-ink/60">
                                  {RHYTHM[x.v.ritmo]}
                                </span>
                              )
                            )}
                          </span>
                          <span className="col-start-2 text-xs text-ink/45">
                            {x.v.riporta_a}
                            {x.v.famiglia && ` · ${FAMILY[x.v.famiglia].titolo.toLowerCase()}`}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {notes.length > 0 && (
        <details className="group text-xs text-ink/50">
          <summary className="flex cursor-pointer list-none items-center gap-2 [&::-webkit-details-marker]:hidden">
            <span className="transition-transform group-open:rotate-90">▸</span>
            Le ipotesi da cui sono partito · {notes.length}
          </summary>
          <ul className="mt-2 space-y-0.5 pl-5">
            {notes.map((n) => (
              <li key={n} className="list-disc">
                {n}
              </li>
            ))}
          </ul>
        </details>
      )}

      {sources.length > 0 && (
        <details className="group text-xs text-ink/50">
          <summary className="flex cursor-pointer list-none items-center gap-2 [&::-webkit-details-marker]:hidden">
            <span className="transition-transform group-open:rotate-90">▸</span>
            Dove ho cercato · {sources.length} {sources.length === 1 ? 'pagina' : 'pagine'}
          </summary>
          <ul className="mt-2 space-y-0.5 pl-5">
            {sources.map((u) => (
              <li key={u} className="list-disc break-all">
                <a
                  href={u}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-accent hover:underline"
                >
                  {u.replace(/^https?:\/\/(www\.)?/, '').replace(/[?#].*$/, '')}
                </a>
              </li>
            ))}
          </ul>
        </details>
      )}

      {items.length > 0 && (
        <AddedItems
          step="procedure"
          feminine
          intro="Diventano compiti quando, dopo l’attivazione, metterai le persone nei ruoli. Se fate qualcosa che qui non vedi, aggiungi la procedura: resta tua, e io la completo con chi la fa, ogni quanto e cosa lascia."
          label="Aggiungi una procedura"
          placeholder="Es. l’inventario di fine anno"
          notePlaceholder="Es. lo fa il magazzino ogni dicembre"
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        {isManager && !updating && items.length > 0 && (
          <button className="text-xs text-accent hover:underline" onClick={() => refresh.mutate()}>
            Rifai il progetto
          </button>
        )}
        <span className="flex-1" />
        {next && (
          <Button onClick={onNext}>
            Avanti<span className="hidden sm:inline">: {next.titolo}</span> →
          </Button>
        )}
        {!next && finish && <Button onClick={finish}>Vedi la tua azienda →</Button>}
      </div>

      <ProcedureSheet item={opened} onClose={() => setOpenId(null)} />
    </div>
  );
}

/** Lo standard di una procedura, a lato: come si fa, con cosa, chi ne risponde, cosa lascia, perché c'è. */
function ProcedureSheet({ item, onClose }: { item: Item | null; onClose: () => void }) {
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!item) return;
    close.current?.focus();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [item, onClose]);
  if (!item) return null;
  const v = item.v;
  const f = v.famiglia ? FAMILY[v.famiglia] : null;
  const facts = [
    ['Ne risponde', v.riporta_a],
    // Chi la svolge (ADR 0137): gli stessi ruoli dell'organigramma.
    ['La svolgono', v.svolge?.length ? v.svolge.join(', ') : null],
    ['Quando', v.quantita ?? (v.ritmo ? RHYTHM[v.ritmo] : null)],
    ['Cosa lascia', v.prova],
    ['Verso', v.ente],
    ['Atlante del Lavoro', v.area_atlante ? `${v.area_atlante.codice} ${v.area_atlante.nome}` : null],
  ].filter((x): x is [string, string] => !!x[1]);
  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/35" onClick={onClose} aria-hidden="true" />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="procedura-titolo"
        className="fixed inset-y-0 right-0 z-50 grid w-full max-w-[520px] content-start gap-4 overflow-y-auto bg-white px-5 py-5 shadow-2xl sm:px-6"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs text-ink/45">
              {item.scope.titolo}
              {v.gruppo && ` · ${cap(v.gruppo)}`}
            </p>
            <h3 id="procedura-titolo" className="mt-1 text-xl font-semibold leading-tight">
              {v.nome}
            </h3>
          </div>
          <button
            ref={close}
            onClick={onClose}
            aria-label="Chiudi"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-black/[0.05] text-lg"
          >
            ×
          </button>
        </div>
        {f && v.rilievo && (
          <p className={`flex gap-2 rounded-xl px-3 py-2.5 text-[13.5px] leading-snug ${f.wash}`}>
            <b className={`whitespace-nowrap ${f.ink}`}>{f.titolo}</b>
            <span>{v.rilievo}</span>
          </p>
        )}
        {v.descrizione && <p className="text-[15px] leading-normal">{v.descrizione}</p>}
        {v.dettagli.length > 0 && (
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink/40">
              Come si fa
            </p>
            <ol className="grid gap-2">
              {v.dettagli.map((s, i) => (
                <li key={s} className="grid grid-cols-[22px_minmax(0,1fr)] gap-2.5 text-sm">
                  <span className="grid h-[22px] w-[22px] place-items-center rounded-full bg-accent/10 text-[11px] font-semibold tabular-nums text-accent">
                    {i + 1}
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
        <Checks v={v} />
        {!!v.strumenti?.length && (
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink/40">
              Si fa con
            </p>
            <div className="flex flex-wrap gap-1.5">
              {v.strumenti.map((x) => (
                <span
                  key={x.nome}
                  className={`rounded-lg px-2.5 py-1 text-[13px] ring-1 ring-inset ${x.risponde ? 'bg-accent/10 text-accent ring-accent' : 'bg-black/[0.03] ring-black/10'}`}
                >
                  {x.nome}
                  {x.risponde && (
                    <small className="block text-[11px] opacity-80">qui resta la prova</small>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}
        {facts.length > 0 && (
          <div className="grid gap-2 sm:grid-cols-3">
            {facts.map(([k, x]) => (
              <div
                key={k}
                className="min-w-0 rounded-xl bg-black/[0.03] px-3 py-2 text-[13.5px] leading-snug"
              >
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink/40">
                  {k}
                </p>
                {x}
              </div>
            ))}
          </div>
        )}
        {v.norma && (
          <p>
            <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-700">
              {v.norma}
            </span>
          </p>
        )}
        {v.perche && (
          <p className="text-sm">
            <span className="text-ink/45">Perché c’è:</span> {v.perche} <ItemBase v={v} detail />
          </p>
        )}
        <LibrarySources refs={v.libreria} fondata={v.fondata} conti={v.conti} />
      </aside>
    </>
  );
}
